/**
 * 🎵 lofiEmbed — the pure URL math behind LofiRadio, extracted so it can be
 * unit-tested. LofiRadio.js is JSX, so these lived untestable inside it while
 * the 08-15 jukebox repeat bug shipped and `buildEmbedUrl` had zero coverage
 * anywhere in the repo (TERMINAL_HANDOFF_2026_08_17.md §2.5). The embed URL is
 * the ENTIRE track-advance mechanism — there is no track-end detection in the
 * frontend — so this file is where the player's real behavior is decided.
 *
 * ⚠️ Keep the deck contract in step with the backend's `jukeboxDeck`
 * (justsomeguy-backend/src/ChatGame/spaceCommands.js): head id first, queue
 * ids deduped against it. The shared-clock offset is only meaningful if both
 * ends walk the same track order (tools/smoke-jukebox.js pins that side).
 */

const DEFAULT_VIDEO_ID = '4xDzrJKXOOY';

/**
 * Parse a YouTube URL into a type + id for embedding.
 * Supports watch, youtu.be, embed, and playlist URLs.
 * Returns null for invalid/non-YouTube URLs.
 */
function parseYouTubeUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  try {
    // ⏱️ START OFFSET (Nina 08-08: "escape to disney should start at 0:09").
    // YouTube's own share-with-timestamp params — `t=` on watch/youtu.be links,
    // `start=` on embeds. Parsed here because everything below throws the rest
    // of the URL away and keeps only the ID, so a pasted timestamped link used
    // to silently lose its offset. Accepts raw seconds (t=9) and YouTube's
    // duration form (t=1m30s). URL-native on purpose: no new DB column, and a
    // space's start time is set by pasting a timestamped link.
    const startSeconds = (() => {
      const m = trimmed.match(/[?&](?:t|start)=([0-9]+h)?([0-9]+m)?([0-9]+s?)?/);
      if (!m) return 0;
      const h = parseInt(m[1] || '0', 10) || 0;
      const min = parseInt(m[2] || '0', 10) || 0;
      const sec = parseInt(m[3] || '0', 10) || 0;
      const total = h * 3600 + min * 60 + sec;
      return Number.isFinite(total) && total > 0 ? total : 0;
    })();

    // Playlist-only URL (no video specified)
    const playlistMatch = trimmed.match(/[?&]list=([a-zA-Z0-9_-]+)/);
    if (playlistMatch && !trimmed.match(/[?&]v=/)) {
      return { type: 'playlist', id: playlistMatch[1], startSeconds };
    }

    // Standard watch URL: youtube.com/watch?v=ID
    const watchMatch = trimmed.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    if (watchMatch) return { type: 'video', id: watchMatch[1], startSeconds };

    // Short URL: youtu.be/ID
    const shortMatch = trimmed.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    if (shortMatch) return { type: 'video', id: shortMatch[1], startSeconds };

    // Embed URL: youtube.com/embed/ID
    const embedMatch = trimmed.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
    if (embedMatch) return { type: 'video', id: embedMatch[1], startSeconds };

    return null;
  } catch {
    return null;
  }
}

function buildEmbedUrl(parsed, withApi = false, queueIds = null) {
  // withApi: enablejsapi lets us postMessage volume commands into the player
  // (scene music ducks under the ambience beds — Nina 07-30). Only appended
  // when a volume will actually be applied, so ordinary radio is untouched.
  const api = withApi ? '&enablejsapi=1' : '';
  // ⏱️ &start=N — YouTube's embed param for "begin playback here" (08-08).
  // Only appended when a timestamp was actually parsed, so every existing
  // music_url builds byte-identical to before.
  const start = parsed?.startSeconds > 0 ? `&start=${parsed.startSeconds}` : '';
  if (!parsed) return `https://www.youtube.com/embed/${DEFAULT_VIDEO_ID}?autoplay=1${api}`;
  if (parsed.type === 'playlist') {
    return `https://www.youtube.com/embed/videoseries?list=${parsed.id}&autoplay=1${api}${start}`;
  }
  // 🎵 J1 house queue: when the space sent a queue, play it as a native
  // YouTube playlist with loop=1 so the whole set cycles forever — no
  // track-end detection needed, and an idle room never falls silent.
  // ⚠️ YouTube plays the `playlist=` videos and IGNORES the path video
  // (measured both directions on a real origin, 08-17,
  // tools/probe-jukebox-embed.js), so the leader goes IN the param, first.
  // Excluding it meant the lead track never played and a 2-track deck looped
  // the one non-leader song forever. Spaces without a queue build
  // byte-identical URLs to before.
  if (queueIds && queueIds.length) {
    const list = [parsed.id, ...queueIds.filter((id) => id !== parsed.id)];
    // 🔁 Loop only at 3+ tracks (Nina 08-18): under three, a looping room is
    // a stuck record, not a station — play the deck through once, then quiet.
    // A fresh /aux add rebuilds the deck and music returns on its own. This
    // also retires the old one-track playlist=self forever-loop.
    const loop = list.length >= 3 ? '&loop=1' : '';
    return `https://www.youtube.com/embed/${parsed.id}?autoplay=1${api}${start}&playlist=${list.join(',')}${loop}`;
  }
  return `https://www.youtube.com/embed/${parsed.id}?autoplay=1${api}${start}`;
}

export { DEFAULT_VIDEO_ID, parseYouTubeUrl, buildEmbedUrl };
